import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Building2, CalendarRange, Mail, MessageCircleQuestion, RefreshCw, Sparkles } from 'lucide-react'
import { Button, EndpointHint, HelpIcon, LoadFailure, Modal, Skeleton, navigate, type SessionRole } from '../../../modules/core/index.js'
import { briefingReadPath, refusalOf, useBriefingActions, useBriefingRecord, useLatestBriefing, type BriefingTarget } from '../hooks.js'
import { INSIGHTS_SPANS, REFUSAL_WORDS, SPAN_WORDS, countdownLabel, secondsUntil, timeLabel, whenLabel } from '../labels.js'
import type { Briefing, BriefingGuard, BriefingQuestion, BriefingSettings } from '../types.js'

/** The footer the API's words are shown under, as the plan writes it (section 9). */
const FOOTER = "Written by AI from the facility's records. It can be wrong; check before acting."

/** Where the dialog was opened: a facility's Dashboard, the facilities list, or the Insights page. */
export type BriefingContext = { kind: 'facility'; facilityId: string } | { kind: 'client' } | { kind: 'insights' }

interface BriefingDialogProps {
  context: BriefingContext
  role: SessionRole
  emailConfigured: boolean
  onClose: () => void
}

/** A second-by-second clock, for the cooldown's countdown. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])
  return now
}

/**
 * A fact's link as this shell reads it: the API writes `/facilities/{id}...`, which is the multi-facility scheme; a
 * single-facility client lives at `/`, so the prefix comes off there.
 */
function localHref(href: string): string {
  if (window.location.pathname.startsWith('/facilities/') || !href.startsWith('/facilities/')) {
    return href
  }
  const stripped = href.replace(/^\/facilities\/[^/?]+/, '')
  return stripped === '' ? '/' : stripped.startsWith('?') ? `/${stripped}` : stripped
}

/** The choices a context offers: the facility and the client for a Dashboard, the client alone for the list, the three spans for Insights. */
function choicesOf(context: BriefingContext): { target: BriefingTarget; label: string; detail: string; icon: typeof Building2 }[] {
  switch (context.kind) {
    case 'facility':
      return [
        { target: { kind: 'facility', facilityId: context.facilityId }, label: 'This facility', detail: 'What changed here since you signed in: what needs you, what happened, the month, the settings.', icon: Building2 },
        { target: { kind: 'client' }, label: 'All my facilities', detail: 'The same across every facility you run, the ones needing you first.', icon: Building2 },
      ]
    case 'client':
      return [{ target: { kind: 'client' }, label: 'All my facilities', detail: 'What changed across every facility you run since you signed in, the ones needing you first.', icon: Building2 }]
    case 'insights':
      return INSIGHTS_SPANS.map((span) => ({ target: { kind: 'insights', span }, label: SPAN_WORDS[span].label, detail: SPAN_WORDS[span].detail, icon: CalendarRange }))
  }
}

/** One choice: what it covers, and whether a briefing of it is already written. Reads the latest, which is cheap and tells the card what pressing it will do. */
function ChoiceCard({ target, label, detail, icon: Icon, onChoose }: { target: BriefingTarget; label: string; detail: string; icon: typeof Building2; onChoose: () => void }) {
  const latest = useLatestBriefing(target, true)
  const briefing = latest.data?.briefing ?? null
  const state = latest.isPending ? 'Checking…' : briefing === null ? 'Not written yet' : briefing.status === 'pending' ? 'Being written' : briefing.status === 'failed' ? 'The last one failed' : briefing.status === 'empty' ? `Nothing to report, as of ${timeLabel(briefing.generatedAt ?? briefing.requestedAt)}` : `Written ${timeLabel(briefing.generatedAt ?? briefing.requestedAt)}`
  return (
    <button type="button" onClick={onChoose} className="flex w-full items-start gap-3 rounded border border-marine/20 bg-white px-4 py-3 text-left hover:border-coral hover:bg-coral-lt/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral">
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-marine" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-ink">{label}</span>
        <span className="block text-[11px] leading-relaxed text-ink-mid">{detail}</span>
        <span className={`mt-1 block text-[10px] font-bold uppercase tracking-wide ${briefing && briefing.status === 'ready' ? 'text-green' : 'text-ink-mid'}`}>{state}</span>
      </span>
    </button>
  )
}

function Sections({ briefing, onOpen }: { briefing: Briefing; onOpen: (href: string) => void }) {
  return (
    <div className="flex flex-col gap-4">
      {briefing.sections.map((section) => (
        <section key={section.title}>
          <h3 className="text-[11px] font-bold uppercase tracking-wide text-marine">{section.title}</h3>
          <ul className="mt-1.5 flex list-disc flex-col gap-1.5 pl-4 marker:text-marine/50">
            {section.items.map((item, index) => (
              <li key={`${section.title}-${index}`} className="text-ink">
                {item.text}
                {item.href && (
                  <>
                    {' '}
                    <button type="button" onClick={() => onOpen(item.href as string)} className="font-bold text-marine hover:text-marine-dk">
                      Open
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function QuestionRow({ question, onOpen }: { question: BriefingQuestion; onOpen: (href: string) => void }) {
  return (
    <li className="rounded border border-marine/15 bg-marine-lt/20 px-3 py-2">
      <div className="text-[11px] font-bold text-ink">{question.question}</div>
      {question.status === 'pending' ? (
        <Skeleton lines={1} label="Answering" className="mt-1" />
      ) : question.status === 'failed' ? (
        <div className="mt-1 text-[11px] text-coral">{question.failure ?? 'The answer could not be written'}</div>
      ) : (
        <div className="mt-1 text-[12px] text-ink">
          {question.answer}
          {question.status === 'outside' && question.hrefs.length > 0 && (
            <>
              {' '}
              <button type="button" onClick={() => onOpen(question.hrefs[0] as string)} className="font-bold text-marine hover:text-marine-dk">
                Where to look
              </button>
            </>
          )}
        </div>
      )}
    </li>
  )
}

function QuestionBox({ briefing, guard, settings, ask, asking, onOpen }: { briefing: Briefing; guard: BriefingGuard; settings: BriefingSettings; ask: (question: string) => void; asking: boolean; onOpen: (href: string) => void }) {
  const [question, setQuestion] = useState('')
  const pending = briefing.questions.some((entry) => entry.status === 'pending')
  const left = Math.max(0, settings.questionsPerBriefing - briefing.questions.length)
  const canAsk = guard.canAsk && !pending && !asking && left > 0 && question.trim().length >= settings.questionMinChars && question.trim().length <= settings.questionChars
  if (settings.questionsPerBriefing <= 0) {
    return null
  }
  return (
    <section className="flex flex-col gap-2">
      <h3 className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-marine">
        <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" /> Ask the briefing
      </h3>
      {briefing.questions.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {briefing.questions.map((entry) => (
            <QuestionRow key={entry.id} question={entry} onOpen={onOpen} />
          ))}
        </ul>
      )}
      {left > 0 ? (
        <form
          className="flex items-start gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (canAsk) {
              ask(question.trim())
              setQuestion('')
            }
          }}
        >
          <input className="min-w-0 flex-1 rounded border border-marine/30 bg-white px-2 py-1.5 text-xs text-ink focus:outline-none focus:border-marine" placeholder="A question about this briefing" value={question} maxLength={settings.questionChars} onChange={(event) => setQuestion(event.target.value)} disabled={pending || asking} aria-label="Your question" />
          <Button type="submit" icon={MessageCircleQuestion} size="sm" disabled={!canAsk} busy={asking || pending}>
            {asking || pending ? 'Asking…' : 'Ask'}
          </Button>
        </form>
      ) : (
        <p className="text-[11px] text-ink-mid">{REFUSAL_WORDS['questions_used']}</p>
      )}
      <p className="text-[10px] text-ink-mid">
        {left} of {settings.questionsPerBriefing} question{settings.questionsPerBriefing === 1 ? '' : 's'} left · answered from this briefing's facts only
      </p>
    </section>
  )
}

/** The briefing of one target: read, written on the first open of a target with none, polled while pending, with Refresh, Email me this and the questions. */
function BriefingView({ target, emailConfigured, onClose }: { target: BriefingTarget; emailConfigured: boolean; onClose: () => void }) {
  const latest = useLatestBriefing(target, true)
  const settings = latest.data?.settings
  const record = useBriefingRecord(latest.data?.briefing?.id ?? null, settings?.pollMs ?? 1500)
  const actions = useBriefingActions(target)
  const briefing = record.data ?? latest.data?.briefing ?? null
  const now = useNow()
  const requested = useRef(false)

  // The reader chose this target, so a target with nothing written is written now; a stored briefing opens with no call.
  useEffect(() => {
    if (latest.data && latest.data.briefing === null && !requested.current && !actions.request.isPending) {
      requested.current = true
      actions.request.mutate(false)
    }
  }, [latest.data, actions.request])

  const guard = latest.data?.guard
  const refusal = refusalOf(actions.request.error)
  const retryAt = refusal?.retryAt ?? guard?.retryAt ?? null
  const waitSeconds = secondsUntil(retryAt, now)
  const refreshReason = useMemo(() => {
    if (refusal && (refusal.refusal === 'cooldown' || refusal.refusal === 'daily_cap' || refusal.refusal === 'client_cap' || refusal.refusal === 'deployment_cap') && waitSeconds > 0) {
      return `${REFUSAL_WORDS[refusal.refusal]} · ${countdownLabel(waitSeconds)}`
    }
    if (refusal) {
      return REFUSAL_WORDS[refusal.refusal] ?? refusal.message
    }
    if (guard && !guard.canRefresh && guard.reason) {
      return guard.reason === 'cooldown' && waitSeconds > 0 ? `Refresh in ${countdownLabel(waitSeconds)}` : (REFUSAL_WORDS[guard.reason] ?? guard.reason)
    }
    return null
  }, [refusal, guard, waitSeconds])
  const canRefresh = (guard?.canRefresh ?? false) && !refusal && !actions.request.isPending
  const emailRefusal = refusalOf(actions.email.error)
  const askRefusal = refusalOf(actions.ask.error)
  const go = (href: string) => {
    onClose()
    navigate(localHref(href))
  }

  if (latest.isError) {
    return <LoadFailure message="Could not read your briefing." onRetry={() => void latest.refetch()} retrying={latest.isFetching} />
  }
  if (!latest.data || (briefing === null && actions.request.isPending) || briefing?.status === 'pending') {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton lines={5} label="Writing your briefing" />
        <p className="text-[11px] text-ink-mid">Reading the records and writing… a few seconds.</p>
      </div>
    )
  }
  if (briefing === null) {
    return refusal ? <p className="text-[12px] text-ink-mid">{REFUSAL_WORDS[refusal.refusal] ?? refusal.message}</p> : <LoadFailure message={actions.request.error?.message ?? 'The briefing could not be requested.'} onRetry={() => actions.request.mutate(false)} retrying={actions.request.isPending} />
  }
  if (briefing.status === 'failed') {
    return <LoadFailure message={briefing.failure ?? 'The briefing could not be written.'} onRetry={() => actions.request.mutate(true)} retrying={actions.request.isPending} />
  }
  const windowLine = briefing.window.basis === 'span' ? briefing.subjectName : briefing.window.basis === 'last_sign_in' ? `Since you signed in ${whenLabel(briefing.window.since)}${briefing.window.capped ? ' (as far back as it reaches)' : ''}` : `The last ${settings?.defaultWindowDays ?? 7} days`
  return (
    <div className="flex flex-col gap-4 text-[13px] leading-relaxed text-ink">
      <p className="text-[11px] text-ink-mid">{windowLine}</p>
      <p className="text-sm font-bold text-ink">{briefing.headline}</p>
      {briefing.status === 'ready' && <Sections briefing={briefing} onOpen={go} />}
      <p className="border-t border-marine/10 pt-3 text-[11px] text-ink-mid">{FOOTER}</p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-ink-mid">Briefing from {timeLabel(briefing.generatedAt ?? briefing.requestedAt)}</span>
        <Button icon={RefreshCw} size="sm" variant="secondary" disabled={!canRefresh} busy={actions.request.isPending} onClick={() => actions.request.mutate(true)} title={refreshReason ?? 'Write a new briefing'}>
          {actions.request.isPending ? 'Refreshing…' : 'Refresh'}
        </Button>
        {emailConfigured &&
          settings?.emailAllowed &&
          briefing.status === 'ready' &&
          (briefing.emailedAt ? (
            <span className="text-[11px] font-bold text-green">Sent to {actions.email.data?.to ?? 'your email'}</span>
          ) : (
            <Button icon={Mail} size="sm" variant="secondary" disabled={!(guard?.canEmail ?? true) || actions.email.isPending} busy={actions.email.isPending} onClick={() => actions.email.mutate(briefing.id)}>
              {actions.email.isPending ? 'Sending…' : 'Email me this'}
            </Button>
          ))}
      </div>
      {refreshReason && !canRefresh && <p className="text-[11px] text-ink-mid">{refreshReason}</p>}
      {emailRefusal && <p className="text-[11px] text-coral">{REFUSAL_WORDS[emailRefusal.refusal] ?? emailRefusal.message}</p>}
      {actions.email.isError && !emailRefusal && <p className="text-[11px] text-coral">{actions.email.error.message}</p>}
      {briefing.status === 'ready' && guard && settings && <QuestionBox briefing={briefing} guard={{ ...guard, canAsk: guard.canAsk || briefing.questions.length < settings.questionsPerBriefing }} settings={settings} ask={(question) => actions.ask.mutate({ briefingId: briefing.id, question })} asking={actions.ask.isPending} onOpen={go} />}
      {askRefusal && <p className="text-[11px] text-coral">{REFUSAL_WORDS[askRefusal.refusal] ?? askRefusal.message}</p>}
      {actions.ask.isError && !askRefusal && <p className="text-[11px] text-coral">{actions.ask.error.message}</p>}
      <p className="text-[10px] text-ink-mid">
        Written by {briefing.model ?? settings?.model ?? 'the model'} from {briefing.factsCount} fact{briefing.factsCount === 1 ? '' : 's'}.
      </p>
    </div>
  )
}

/**
 * The briefing dialog (docs/plans/ai-briefings-plan.md section 13, as the user asked on 29 October 2026):
 * centred, and it writes nothing until a choice is made - the facility or every facility since the sign-in from
 * a Dashboard, the client from the facilities list, this month, quarter or year of the Insights reports from the
 * Insights page. A choice already written opens at once; the rest of the dialog is the briefing with Refresh under
 * the guard, Email me this and the question box. Every number it shows is the API's.
 */
export function BriefingDialog({ context, role, emailConfigured, onClose }: BriefingDialogProps) {
  const choices = useMemo(() => choicesOf(context), [context])
  const [chosen, setChosen] = useState<BriefingTarget | null>(null)
  const title = chosen ? (choices.find((choice) => JSON.stringify(choice.target) === JSON.stringify(chosen))?.label ?? 'Your briefing') : context.kind === 'insights' ? 'Summarise the Insights reports' : 'What changed since you signed in'
  return (
    <Modal
      eyebrow="AI briefing"
      title={title}
      subtitle={
        <span className="inline-flex items-center gap-2">
          {chosen ? (context.kind === 'insights' ? 'Every report over the span, summarised.' : role === 'operator' ? 'Written from the records, for you alone.' : 'Written from your facilities\' records.') : 'Choose what to brief; nothing is written until you do.'}
          <HelpIcon topic="ai_briefings.panel" />
          <EndpointHint>{chosen ? `GET ${briefingReadPath(chosen).replace(/\?.*$/, '')}` : 'POST /briefing'}</EndpointHint>
        </span>
      }
      onClose={onClose}
      footer={
        chosen ? (
          <button type="button" onClick={() => setChosen(null)} className="inline-flex items-center gap-1 text-xs font-bold text-marine hover:text-marine-dk">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Back to the choices
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-ink-mid">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> A briefing is read, not refreshed: one per sign-in, or per span, until there is something new.
          </span>
        )
      }
    >
      <div className="px-6 py-5">
        {chosen ? (
          <BriefingView target={chosen} emailConfigured={emailConfigured} onClose={onClose} />
        ) : (
          <div className="flex flex-col gap-2">
            {choices.map((choice) => (
              <ChoiceCard key={targetKeyOf(choice.target)} target={choice.target} label={choice.label} detail={choice.detail} icon={choice.icon} onChoose={() => setChosen(choice.target)} />
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

function targetKeyOf(target: BriefingTarget): string {
  return target.kind === 'facility' ? `facility:${target.facilityId}` : target.kind === 'client' ? 'client' : `insights:${target.span}`
}
